import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Fragment, useMemo, useState } from 'react';

interface Vehicle {
  id: string;
  plate: string;
  brand?: string;
  model?: string;
  year?: string;
}

interface VehicleComboboxProps {
  vehicles: Vehicle[];
  selectedVehicle: Vehicle | null;
  onSelect: (vehicle: Vehicle | null) => void;
  onCreateVehicle?: (query: string) => void;
  placeholder?: string;
  disabled?: boolean;
  getVehicleLabel?: (vehicle: any) => string;
}

const vehicleLabel = (vehicle: Vehicle) => {
  const brandModel = [vehicle.brand, vehicle.model].filter(Boolean).join(' ').trim();
  const year = vehicle.year ? ` (${vehicle.year})` : '';
  if (!brandModel) return `${vehicle.plate}${year}`;
  return `${vehicle.plate} - ${brandModel}${year}`;
};

export default function VehicleCombobox({
  vehicles,
  selectedVehicle,
  onSelect,
  onCreateVehicle,
  placeholder = 'Buscar vehículo...',
  disabled = false,
  getVehicleLabel
}: VehicleComboboxProps) {
  const [query, setQuery] = useState('');

  const labelFn = getVehicleLabel || vehicleLabel;

  const filteredVehicles = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return vehicles;
    return vehicles.filter((v) => {
      const plate = String(v.plate || '').toLowerCase();
      const brand = String(v.brand || '').toLowerCase();
      const model = String(v.model || '').toLowerCase();
      const year = String(v.year || '').toLowerCase();
      const full = labelFn(v).toLowerCase();
      return (
        plate.includes(normalized) ||
        brand.includes(normalized) ||
        model.includes(normalized) ||
        year.includes(normalized) ||
        full.includes(normalized)
      );
    });
  }, [vehicles, query, labelFn]);

  const trimmedQuery = query.trim();
  const showCreateOption = !!onCreateVehicle && !disabled && trimmedQuery.length > 0;

  const handleChange = (vehicle: Vehicle | null) => {
    if (vehicle?.id === '__create__') {
      onCreateVehicle?.(query.trim());
      return;
    }
    onSelect(vehicle);
  };

  return (
    <Combobox value={selectedVehicle} onChange={handleChange} disabled={disabled} by="id">
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            className="w-full h-auto px-4 py-3 pl-10 pr-10 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={placeholder}
            displayValue={(vehicle: Vehicle | null) => (vehicle ? labelFn(vehicle) : '')}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {selectedVehicle && !disabled && (
            <button
              type="button"
              onClick={() => { onSelect(null); setQuery(''); }}
              className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Quitar vehiculo"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <Combobox.Button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg shadow-lg border bg-white">
          {filteredVehicles.length === 0 && trimmedQuery.length > 0 && !showCreateOption && (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <p className="font-medium mb-1">No se encontraron vehiculos</p>
              <p className="text-sm">Intenta buscar por patente, marca o modelo</p>
            </div>
          )}

          {filteredVehicles.map((vehicle) => (
            <Combobox.Option key={vehicle.id} value={vehicle} as={Fragment}>
              {({ active, selected }) => (
                <li className={`relative cursor-pointer select-none px-4 py-2 transition-colors ${active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${selected ? 'font-semibold' : 'font-normal'}`}>{labelFn(vehicle)}</span>
                    {selected && <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />}
                  </div>
                </li>
              )}
            </Combobox.Option>
          ))}

          {showCreateOption && (
            <Combobox.Option value={{ id: '__create__', plate: trimmedQuery }} as={Fragment}>
              {({ active }) => (
                <li className={`relative cursor-pointer select-none px-4 py-2 text-sm transition-colors border-t ${active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                  Crear vehiculo...
                </li>
              )}
            </Combobox.Option>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}
